import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrencyService, SupportedCurrency } from '../../core/services/currency.service';

export const FREE_SHIPPING_THRESHOLDS: Record<SupportedCurrency, number> = {
  EUR: 999,
  PLN: 1999,
};
export const DEFAULT_BASE_SHIPPING = 8.0; // Standard base delivery price
export const RATE_PER_ADDITIONAL_KG = 0.5; // Additional fee per kg over 1kg
export const FRAGILE_SURCHARGE = 3.0; // Surcharge for fragile items
export const BULKY_SURCHARGE = 5.0; // Surcharge for bulky cargo
export const VOLUMETRIC_DIVISOR = 5000; // Standard dimensional weight divisor (L × W × H ÷ 5000)

export interface ProductShippingInfo {
  id?: number | string;
  deliveryPrice?: number | null;
  deliveryDuplicationFactor?: number | null;
  deliveryDays?: string | null;
  weight?: number | null; // Weight in kg
  length?: number | null; // Length in cm
  width?: number | null; // Width in cm
  height?: number | null; // Height in cm
  shippingClass?: string | null; // 'STANDARD' | 'FRAGILE' | 'BULKY' | 'EXPRESS' | 'FREE_SHIPPING'
  isFreeShipping?: boolean | null;
  packageType?: string | null; // 'CARTON' | 'PALLET' | 'ENVELOPE' | 'BULK'
  priceHT?: number | string | null;
}

export interface CartShippingItem {
  quantity?: number;
  price?: number;
  product?: ProductShippingInfo;
  deliveryPrice?: number | null;
  deliveryDuplicationFactor?: number | null;
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  shippingClass?: string | null;
  isFreeShipping?: boolean | null;
  packageType?: string | null;
}

export interface RecipientAddress {
  countryCode: string;
  postalCode?: string;
  city?: string;
  state?: string;
  stateOrProvinceCode?: string;
  streetLine?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ShippingService {
  constructor(
    private http: HttpClient,
    private readonly currencyService: CurrencyService,
  ) {}

  get FREE_SHIPPING_THRESHOLD(): number {
    return this.getFreeShippingThreshold();
  }
  readonly DEFAULT_BASE_SHIPPING = DEFAULT_BASE_SHIPPING;
  readonly RATE_PER_ADDITIONAL_KG = RATE_PER_ADDITIONAL_KG;
  readonly FRAGILE_SURCHARGE = FRAGILE_SURCHARGE;
  readonly BULKY_SURCHARGE = BULKY_SURCHARGE;
  readonly VOLUMETRIC_DIVISOR = VOLUMETRIC_DIVISOR;

  /**
   * Calculates volumetric (dimensional) weight in kg from cm dimensions.
   * Formula: (Length * Width * Height) / 5000
   */
  getVolumetricWeight(
    length?: number | null,
    width?: number | null,
    height?: number | null,
  ): number {
    const l = Number(length) || 0;
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    if (l <= 0 || w <= 0 || h <= 0) return 0;
    return Number(((l * w * h) / this.VOLUMETRIC_DIVISOR).toFixed(3));
  }

  /**
   * Calculates billable weight for a product: max(actual weight, dimensional weight)
   */
  getBillableWeight(product?: ProductShippingInfo | null): number {
    if (!product) return 0;
    const actualWeight = Number(product.weight) || 0;
    const volWeight = this.getVolumetricWeight(
      product.length,
      product.width,
      product.height,
    );
    return Math.max(actualWeight, volWeight);
  }

  /**
   * Calculates total billable weight of all items in cart (in kg)
   */
  getTotalCartWeight(items: CartShippingItem[]): number {
    if (!items || !items.length) return 0;
    return items.reduce((total, item) => {
      const p = item.product || (item as unknown as ProductShippingInfo);
      const qty = Number(item.quantity) || 1;
      const w = this.getBillableWeight(p);
      return total + w * qty;
    }, 0);
  }

  getFreeShippingCurrency(destinationCountry?: string | null): SupportedCurrency {
    const country = this.getCountryCode(destinationCountry);
    return ['PL', 'POLAND', 'POLSKA'].includes(country) ? 'PLN' : 'EUR';
  }

  getFreeShippingThreshold(destinationCountry?: string | null): number {
    return FREE_SHIPPING_THRESHOLDS[this.getFreeShippingCurrency(destinationCountry)];
  }

  formatFreeShippingThreshold(destinationCountry?: string | null): string {
    const currency = this.getFreeShippingCurrency(destinationCountry);
    return this.formatFreeShippingThresholdForCurrency(currency);
  }

  formatFreeShippingThresholdForCurrency(currency: SupportedCurrency): string {
    return this.currencyService.format(FREE_SHIPPING_THRESHOLDS[currency], currency);
  }

  formatFreeShippingAmount(amount: number, destinationCountry?: string | null): string {
    if (!Number.isFinite(amount)) return 'Conversion unavailable - try again later';
    return this.currencyService.format(amount, this.getFreeShippingCurrency(destinationCountry));
  }

  private getSubtotalForFreeShipping(
    subtotalEur: number,
    destinationCountry?: string | null,
  ): number {
    const currency = this.getFreeShippingCurrency(destinationCountry);
    return currency === 'PLN'
      ? this.currencyService.convertFromEur(subtotalEur, 'PLN')
      : subtotalEur;
  }

  /**
   * Determines if the current cart qualifies for free shipping
   */
  isEligibleForFreeShipping(
    items: CartShippingItem[] = [],
    subtotalHT: number = 0,
    destinationCountry?: string | null,
  ): boolean {
    if (!items || !items.length) return false;

    // Check if every product has free shipping override
    const allFree = items.every((item) => {
      const p = item.product || (item as unknown as ProductShippingInfo);
      return p?.isFreeShipping === true || p?.shippingClass === 'FREE_SHIPPING';
    });
    if (allFree) return true;

    // Check if subtotal qualifies for threshold AND no bulky items override free shipping
    const thresholdSubtotal = this.getSubtotalForFreeShipping(subtotalHT, destinationCountry);
    if (Number.isFinite(thresholdSubtotal) &&
      thresholdSubtotal >= this.getFreeShippingThreshold(destinationCountry)) {
      const hasBulky = items.some((item) => {
        const p = item.product || (item as unknown as ProductShippingInfo);
        return p?.shippingClass === 'BULKY';
      });
      return !hasBulky;
    }

    return false;
  }

  /**
   * Calculates remaining amount needed to achieve free shipping
   */
  getFreeShippingRemaining(subtotalHT: number = 0, destinationCountry?: string | null): number {
    const subtotal = this.getSubtotalForFreeShipping(subtotalHT, destinationCountry);
    if (!Number.isFinite(subtotal)) return Number.NaN;
    const remaining = this.getFreeShippingThreshold(destinationCountry) - subtotal;
    return remaining > 0 ? Number(remaining.toFixed(2)) : 0;
  }

  /**
   * Calculates percentage progress towards free shipping (0 - 100%)
   */
  getFreeShippingProgress(subtotalHT: number = 0, destinationCountry?: string | null): number {
    const subtotal = this.getSubtotalForFreeShipping(subtotalHT, destinationCountry);
    if (!Number.isFinite(subtotal) || subtotal <= 0) return 0;
    const pct = (subtotal / this.getFreeShippingThreshold(destinationCountry)) * 100;
    return Number(Math.min(100, pct).toFixed(2));
  }

  /**
   * Calculate single item shipping for display/estimation
   */
  calculateItemShipping(item: CartShippingItem, qty: number = 1): number {
    const p = item.product || (item as unknown as ProductShippingInfo);
    if (!p) return this.DEFAULT_BASE_SHIPPING;

    if (p.isFreeShipping || p.shippingClass === 'FREE_SHIPPING') {
      return 0;
    }

    const quantity = Math.max(1, qty);
    let base = 0;

    if (p.deliveryPrice != null && Number(p.deliveryPrice) > 0) {
      const price = Number(p.deliveryPrice);
      const factor = Number(p.deliveryDuplicationFactor) || 1.5;
      base = quantity === 1 ? price : price * (1 + (quantity - 1) / factor);
    } else {
      const billableWeight = this.getBillableWeight(p) * quantity;
      base = this.DEFAULT_BASE_SHIPPING;
      if (billableWeight > 1) {
        base += (billableWeight - 1) * this.RATE_PER_ADDITIONAL_KG;
      }
    }

    if (p.shippingClass === 'FRAGILE') base += this.FRAGILE_SURCHARGE;
    if (p.shippingClass === 'BULKY') base += this.BULKY_SURCHARGE;

    return Number(base.toFixed(2));
  }

  /**
   * Resolves country name or code to uppercase 2-letter ISO country code
   */
  getCountryCode(countryNameOrCode?: string | null): string {
    if (countryNameOrCode) return countryNameOrCode.trim().toUpperCase();

    try {
      const saved = localStorage.getItem('brocom_selected_country');
      if (!saved) return '';
      const parsed = JSON.parse(saved);
      return (parsed?.code || parsed?.name || '').trim().toUpperCase();
    } catch (e) {
      return '';
    }
  }

  /**
   * Returns base shipping cost and rate per extra kg based on destination
   */
  getCountryShippingRates(_countryCode?: string): { base: number; perKg: number } {
    return { base: this.DEFAULT_BASE_SHIPPING, perKg: this.RATE_PER_ADDITIONAL_KG };
  }

  /**
   * Main Cart Shipping Calculation Engine (Destination & Weight Aware)
   */
  calculateShipping(
    items: CartShippingItem[] = [],
    subtotalHT: number = 0,
    destinationCountry?: string | null,
  ): number {
    if (!items || !items.length) return 0;

    // Free shipping check
    if (this.isEligibleForFreeShipping(items, subtotalHT, destinationCountry)) {
      return 0;
    }

    const countryCode = this.getCountryCode(destinationCountry);
    const { base: baseShipping, perKg: ratePerKg } = this.getCountryShippingRates(countryCode);

    let totalDelivery = 0;
    let hasExplicitProductPrices = false;
    let totalBillableWeight = 0;
    let hasFragile = false;
    let hasBulky = false;

    for (const item of items) {
      const product = item.product || (item as unknown as ProductShippingInfo);
      const qty = Math.max(1, Number(item.quantity) || 1);

      if (product?.isFreeShipping || product?.shippingClass === 'FREE_SHIPPING') {
        continue;
      }

      if (product?.shippingClass === 'FRAGILE') hasFragile = true;
      if (product?.shippingClass === 'BULKY') hasBulky = true;

      const customPrice = product?.deliveryPrice;
      if (customPrice != null && Number(customPrice) > 0) {
        hasExplicitProductPrices = true;
        const basePrice = Number(customPrice);
        const factor = Number(product?.deliveryDuplicationFactor) || 1.5;

        if (qty === 1) {
          totalDelivery += basePrice;
        } else {
          totalDelivery += basePrice * (1 + (qty - 1) / factor);
        }
      } else {
        const itemWeight = this.getBillableWeight(product);
        totalBillableWeight += itemWeight * qty;
      }
    }

    if (hasExplicitProductPrices && totalBillableWeight === 0) {
      if (hasFragile) totalDelivery += this.FRAGILE_SURCHARGE;
      if (hasBulky) totalDelivery += this.BULKY_SURCHARGE;
      return Number(totalDelivery.toFixed(2));
    }

    let weightBasedShipping = 0;
    if (totalBillableWeight > 0 || !hasExplicitProductPrices) {
      weightBasedShipping = baseShipping;
      if (totalBillableWeight > 1) {
        const extraWeight = totalBillableWeight - 1;
        weightBasedShipping += extraWeight * ratePerKg;
      }
    }

    let grandTotal = totalDelivery + weightBasedShipping;
    if (hasFragile) grandTotal += this.FRAGILE_SURCHARGE;
    if (hasBulky) grandTotal += this.BULKY_SURCHARGE;

    return Number(grandTotal.toFixed(2));
  }

  /**
   * Fetches real-time rate quotes from backend (FedEx + fallback)
   */
  getLiveRates(
    items: CartShippingItem[],
    recipient?: RecipientAddress,
    subtotalHT?: number
  ): Observable<any> {
    return this.http.post(`${environment.apiUrl}/shipping/calculate-rate`, {
      items,
      recipient,
      subtotalHT,
    });
  }
}
